import { useContext, useEffect, useState } from 'react'
import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import PlayIcon from '@/CustomIcons/PlayIcon'
import StopIcon from '@/CustomIcons/StopIcon'
import useMediaSourceActions from '@/functionalities/MediaSources/useMediaSourceActions'
import { triggerPermissionPromptOnWebapp } from '@/services/MediaDevices'
import { Button } from '@/UIComponents2/Button'
import { UserContext, UserContextType } from '@/UserContext'
import { SpeakerSelect } from '@/components/SpeakerSelect'
import { TalkbackMicSelect } from '@/components/TalkbackMicSelect'
import { CameraPreview } from '@/components/CameraPreview'
import { CameraSelect } from '@/components/CameraSelect'
import MicrophoneIcon from '@/CustomIcons/MicrophoneIcon'
import { OverlayControlButton } from '@/components/OverlayControlButton'
import CameraIcon from '@/CustomIcons/CameraIcon'
import Tooltip from '@/UIComponents2/Tooltip'
import useRoomActions from '@/services/useRoomActions'
import VirtualBackgroundIcon from '@/CustomIcons/VirtualBackgroundIcon'
import { useAppContext } from '@/AppContext'
import MicrophoneOffIcon from '@/CustomIcons/MicrophoneOffIcon'
import { env } from '@/services/EnvironmentVariables'
import { useTranslation } from 'react-i18next'
import CameraOffIcon from '@/CustomIcons/CameraOffIcon'
import useMiscRoomActions from '@/services/useMiscRoomActions'
import GearIcon from '@/CustomIcons/GearIcon'

export function BasicAVTab() {
    const { t } = useTranslation()
    const { state: userState } = useContext(UserContext) as UserContextType
    const { chooseDefaultMediaSelection } = useMediaSelectionActions()
    const { loadAudioAndVideoMediaSources } = useMediaSourceActions()

    const [isPermissionsAlertOpen, setIsPermissionsAlertOpen] = useState(false)
    const [isPermissionsDenied, setIsPermissionsDenied] = useState(false)
    const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false)
    const [playingTest, setPlayingTest] = useState(false)

    const loadMediaSources = async () => {
        if (!env.IS_ELECTRON) {
            const hasPermissionPromise = triggerPermissionPromptOnWebapp()
            const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 1500))

            Promise.race([hasPermissionPromise, timeoutPromise]).then((result) => {
                if (result === false) {
                    setIsPermissionsAlertOpen(false)
                    setIsPermissionsDenied(true)
                    setIsBlockedModalOpen(true)
                } else if (result !== true) {
                    setIsPermissionsAlertOpen(true)
                }
            })

            const hasPermission = await hasPermissionPromise
            setIsPermissionsAlertOpen(false)
            if (!hasPermission) {
                setIsPermissionsDenied(true)
            }
        }

        await loadAudioAndVideoMediaSources()
    }

    useEffect(() => {
        if (!userState.mediaSources.loaded) {
            loadMediaSources()
        }
    }, [])

    useEffect(() => {
        if (!userState.mediaSelection.isDefaultMediaSelectionLoaded && userState.mediaSources.loaded) {
            chooseDefaultMediaSelection(userState.mediaSources)
        }
    }, [userState.mediaSources, userState.mediaSelection.isDefaultMediaSelectionLoaded])

    return (
        <div className="w-full flex flex-col gap-4 items-center md:px-[5vw] lg:px-12">
            <div className="relative w-full">
                <CameraPreview videoTrack={userState.mediaStreams.video} isPermissionsDenied={isPermissionsDenied} />
                <CameraPreviewControlsOverlay />
            </div>

            <CameraSelect className="w-full" />

            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
                <TalkbackMicSelect />
                <SpeakerSelect playingTest={playingTest} onTestEnded={() => setPlayingTest(false)} />
            </div>

            <Button onClick={() => setPlayingTest(!playingTest)} className="t-base-light flex items-center gap-1">
                {playingTest ? <StopIcon /> : <PlayIcon />}
                {playingTest ? t('Stop test') : t('Test speaker')}
            </Button>
        </div>
    )
}

function CameraPreviewControlsOverlay() {
    const { t } = useTranslation()
    const [appState, appDispatch] = useAppContext()
    const { calcIsMyAudioDeviceAllMuted, toggleMyAudioDeviceMute, toggleMyVideoDeviceMute, isMyVideoDeviceMuted } =
        useRoomActions()
    const isMuted = calcIsMyAudioDeviceAllMuted()
    const videoMuted = isMyVideoDeviceMuted()
    const { openSettingsModal } = useMiscRoomActions()

    const toggleVirtualBackgroundModal = () => {
        appDispatch({
            type: 'update_app_state',
            value: { showChooseVirtualBackgroundModal: !appState.appState.showChooseVirtualBackgroundModal },
        })
    }

    return (
        <>
            <div className="absolute bottom-0 left-0 right-0 flex pb-2 gap-2 items-center justify-center z-10">
                <Tooltip text={isMuted ? t('Unmute microphone') : t('Mute microphone')}>
                    <div>
                        <OverlayControlButton
                            className={isMuted ? 'text-muse-danger' : ''}
                            onClick={() => toggleMyAudioDeviceMute()}
                        >
                            {isMuted ? (
                                <MicrophoneOffIcon style={{ fontSize: '1.3rem' }} />
                            ) : (
                                <MicrophoneIcon style={{ fontSize: '1.3rem' }} />
                            )}
                        </OverlayControlButton>
                    </div>
                </Tooltip>

                <Tooltip text={videoMuted ? t('Turn on camera') : t('Turn off camera')}>
                    <div>
                        <OverlayControlButton onClick={() => toggleMyVideoDeviceMute()}>
                            {videoMuted ? (
                                <CameraOffIcon className="text-muse-danger" style={{ fontSize: '1.3rem' }} />
                            ) : (
                                <CameraIcon style={{ fontSize: '1.3rem' }} />
                            )}
                        </OverlayControlButton>
                    </div>
                </Tooltip>
            </div>

            <div className="absolute top-2 right-2 z-10 flex gap-2">
                <Tooltip text={t('Video Settings')}>
                    <div>
                        <OverlayControlButton onClick={() => openSettingsModal('video')}>
                            <GearIcon style={{ fontSize: '1.3rem' }} />
                        </OverlayControlButton>
                    </div>
                </Tooltip>
                <Tooltip text={t('Virtual background')}>
                    <div>
                        <OverlayControlButton onClick={() => toggleVirtualBackgroundModal()}>
                            <VirtualBackgroundIcon style={{ fontSize: '1.3rem' }} />
                        </OverlayControlButton>
                    </div>
                </Tooltip>
            </div>
        </>
    )
}
